import { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import { geometryApi } from "../../api/blades";

import { useChartColors } from "../../chartTheme";

const AXIS_BASE = {
  gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af",
  tickfont: { size: 10 }, zeroline: false,
  showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot",
};
const LEGEND_PROPS = {
  font: { size: 9 }, bgcolor: "rgba(255,255,255,0.88)",
  bordercolor: "#e5e7eb", borderwidth: 1,
  x: 0, xanchor: "left", y: 1, yanchor: "top",
};

function ViewChart({ title, xLabel, yLabel, traces, height = 320, layoutExtra = {} }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{title}</p>
      <div style={{ height }}>
        <Plot
          data={traces}
          layout={{
            margin: { l: 55, r: 14, t: 10, b: 45 },
            xaxis: { ...AXIS_BASE, title: xLabel },
            yaxis: { ...AXIS_BASE, title: yLabel },
            paper_bgcolor: "transparent",
            plot_bgcolor: "#f9fafb",
            showlegend: true,
            legend: LEGEND_PROPS,
            hovermode: "closest",
            dragmode: "pan",
            ...layoutExtra,
          }}
          config={{
            displayModeBar: "hover",
            modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
            modeBarButtonsToRemove: ["select2d", "lasso2d"],
            scrollZoom: true,
            responsive: true,
            displaylogo: false,
          }}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler
        />
      </div>
    </div>
  );
}

export default function View2DPage() {
  const { LINE_COLORS } = useChartColors();
  const { activeDesign } = useProjectStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeDesign?.id) return;
    setLoading(true);
    setError(null);
    geometryApi.bladeViews(activeDesign.id)
      .then(({ data }) => setData(data))
      .catch(e => setError(e?.response?.data?.error || "Computation failed"))
      .finally(() => setLoading(false));
  }, [activeDesign?.id, activeDesign?.updated_at]);

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;
  if (loading) return <div className="p-6 text-sm text-gray-400">Computing views…</div>;
  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;
  if (!data) return null;

  // Map line name → color index (consistent between raw and processed)
  const colorByName = {};
  (data.hub_shroud || []).forEach((l, i) => { colorByName[l.name] = LINE_COLORS[i % LINE_COLORS.length]; });

  const processed = data.processed_lines || [];
  const hasProcessed = processed.length > 0;

  const cutoffOverlay = data.cutoff_line ? [{
    type: "scatter", mode: "lines+markers", name: "Cut-off",
    x: data.cutoff_line.r, y: data.cutoff_line.z,
    line: { color: "#111827", width: 2, dash: "dash" },
    marker: { color: "#111827", size: 7 },
  }] : [];

  const hubShroudTraces = [
    // Raw MECO polylines — faded when processed lines are available
    ...(data.hub_shroud || []).map((l, i) => ({
      type: "scatter", mode: "lines",
      name: hasProcessed ? `${l.name} (MECO)` : l.name,
      x: l.r, y: l.z,
      line: {
        color: hasProcessed ? LINE_COLORS[i % LINE_COLORS.length] + "40" : LINE_COLORS[i % LINE_COLORS.length],
        width: hasProcessed ? 1 : 1.8,
        dash: hasProcessed ? "dot" : "solid",
      },
      showlegend: !hasProcessed,
    })),
    // Steps 1-6 result: trimmed & redistributed skeleton lines
    ...processed.map(l => ({
      type: "scatter", mode: "lines", name: l.name,
      x: l.r, y: l.z,
      line: { color: colorByName[l.name] || LINE_COLORS[0], width: 2 },
    })),
    // LE / TE edge lines + markers
    ...(hasProcessed ? [
      {
        type: "scatter", mode: "lines", name: "LE edge", showlegend: false,
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        line: { color: "#111827", width: 1, dash: "solid" },
        hoverinfo: "skip",
      },
      {
        type: "scatter", mode: "lines", name: "TE edge", showlegend: false,
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        line: { color: "#111827", width: 1, dash: "solid" },
        hoverinfo: "skip",
      },
      {
        type: "scatter", mode: "markers", name: "LE",
        x: processed.map(l => l.le_r), y: processed.map(l => l.le_z),
        text: processed.map(l => l.name),
        marker: {
          symbol: "circle", size: 8, color: "white",
          line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 },
        },
        hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>LE</extra>",
      },
      {
        type: "scatter", mode: "markers", name: "TE",
        x: processed.map(l => l.te_r), y: processed.map(l => l.te_z),
        text: processed.map(l => l.name),
        marker: {
          symbol: "square", size: 8, color: "white",
          line: { color: processed.map(l => colorByName[l.name] || "#333"), width: 2 },
        },
        hovertemplate: "%{text}<br>r=%{x:.2f}  z=%{y:.2f}<extra>TE</extra>",
      },
    ] : []),
  ];

  const DEG2RAD = Math.PI / 180;

  const wrapTraces = [
    ...(data.wrap || []).map((l, i) => {
      const color = LINE_COLORS[i % LINE_COLORS.length];
      return {
        type: "scatter", mode: "lines", name: l.name,
        x: l.theta_deg.map((t, j) => l.r[j] * Math.cos(t * DEG2RAD)),
        y: l.theta_deg.map((t, j) => l.r[j] * Math.sin(t * DEG2RAD)),
        legendgroup: l.name,
        line: { color, width: 1.8, shape: "spline" },
      };
    }),
    ...(data.thickness_lines || []).flatMap((l, i) => {
      const color = colorByName[l.name] || LINE_COLORS[i % LINE_COLORS.length];
      return [
        { type: "scatter", mode: "lines", name: `${l.name} PS`,
          x: l.ps_x, y: l.ps_y, legendgroup: l.name, showlegend: false,
          line: { color, width: 0.8, shape: "spline" } },
        { type: "scatter", mode: "lines", name: `${l.name} SS`,
          x: l.ss_x, y: l.ss_y, legendgroup: l.name, showlegend: false,
          line: { color, width: 0.8, shape: "spline" } },
      ];
    }),
  ];

  const confTraces = (data.conf_map || []).map((l, i) => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.m, y: l.s,
    line: { color: LINE_COLORS[i % LINE_COLORS.length], width: 1.8, shape: "spline" },
  }));

  const coneTraces = data.cone_area ? [{
    type: "scatter", mode: "lines", name: "Hub",
    x: data.cone_area.m, y: data.cone_area.area,
    line: { color: "#111827", width: 1.5, shape: "spline" },
  }] : [];

  // Porcupine curvature: normal vectors scaled by normalized curvature κ
  // κ = r'z'' - z'r''  (signed, arc-length parameterisation)
  // Vectors drawn from curve points along unit normal N = (-z', r')
  const CURV_SCALE = 30; // max normal length [mm]

  function curvatureTraces(lines) {
    const hubLine    = lines.find(l => l.name.toUpperCase().includes("HUB"));
    const shroudLine = lines.find(l => l.name.toUpperCase().includes("SHROUD"));
    const targets    = [hubLine, shroudLine].filter(Boolean);
    if (targets.length === 0) return [];

    // Compute signed curvature for one line; returns {kappa[], nr[], nz[]}
    function curvOf(r, z) {
      const n = r.length;
      if (n < 4) return null;

      // Arc-length step (equidistant from _trim_and_redistribute)
      let totalS = 0;
      for (let i = 1; i < n; i++) {
        const dr = r[i] - r[i-1], dz = z[i] - z[i-1];
        totalS += Math.sqrt(dr*dr + dz*dz);
      }
      const h = totalS / (n - 1);

      const kappa = new Float64Array(n);
      const nr    = new Float64Array(n);
      const nz    = new Float64Array(n);

      for (let i = 1; i < n - 1; i++) {
        const drds  = (r[i+1] - r[i-1]) / (2*h);
        const dzds  = (z[i+1] - z[i-1]) / (2*h);
        const d2rds = (r[i+1] - 2*r[i] + r[i-1]) / (h*h);
        const d2zds = (z[i+1] - 2*z[i] + z[i-1]) / (h*h);
        kappa[i] = drds * d2zds - dzds * d2rds;
        nr[i]    = -dzds;   // unit normal = rotate tangent 90°
        nz[i]    =  drds;
      }
      // Extend endpoints
      kappa[0] = kappa[1];   nr[0] = nr[1];   nz[0] = nz[1];
      kappa[n-1] = kappa[n-2]; nr[n-1] = nr[n-2]; nz[n-1] = nz[n-2];

      // Smooth κ with a moving average to suppress numerical noise
      // from second-derivative amplification
      const W = 12; // half-window (full window = 2W+1)
      const smoothed = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        let sum = 0, cnt = 0;
        for (let j = Math.max(0, i - W); j <= Math.min(n - 1, i + W); j++) {
          sum += kappa[j]; cnt++;
        }
        smoothed[i] = sum / cnt;
      }

      return { kappa: smoothed, nr, nz };
    }

    // Global max |κ| across hub + shroud for shared normalisation
    const curves = targets.map(l => ({ line: l, c: curvOf(l.r, l.z) })).filter(x => x.c);
    const maxK   = Math.max(...curves.flatMap(({ c }) => Array.from(c.kappa).map(Math.abs)));
    if (maxK < 1e-12) return [];

    return curves.flatMap(({ line, c }) => {
      const { r, z } = line;
      const rSegs = [], zSegs = [];
      for (let i = 0; i < r.length; i++) {
        const kn = c.kappa[i] / maxK;           // normalised [-1, 1]
        rSegs.push(r[i], r[i] + CURV_SCALE * kn * c.nr[i], null);
        zSegs.push(z[i], z[i] + CURV_SCALE * kn * c.nz[i], null);
      }
      const color = colorByName[line.name] || LINE_COLORS[0];
      return [
        {
          type: "scatter", mode: "lines", name: `${line.name} κ`,
          x: rSegs, y: zSegs,
          line: { color, width: 0.8, dash: "solid" },
          hoverinfo: "skip",
        },
      ];
    });
  }

  const curvTraces = curvatureTraces(processed);

  // Dedicated cone-pairs chart: hub line + shroud line + connecting segments
  const coneMecoTraces = (() => {
    const ca = data.cone_area;
    if (!ca?.hub_r || !ca?.shr_r) return [];

    const r_segs = [], z_segs = [];
    for (let j = 0; j < ca.hub_r.length; j++) {
      r_segs.push(ca.hub_r[j], ca.shr_r[j], null);
      z_segs.push(ca.hub_z[j], ca.shr_z[j], null);
    }

    return [
      {
        type: "scatter", mode: "lines", name: "Hub",
        x: ca.hub_r, y: ca.hub_z,
        line: { color: LINE_COLORS[0], width: 2 },
      },
      {
        type: "scatter", mode: "lines", name: "Shroud",
        x: ca.shr_r, y: ca.shr_z,
        line: { color: LINE_COLORS[LINE_COLORS.length - 1] || LINE_COLORS[1], width: 2 },
      },
      {
        type: "scatter", mode: "lines", name: "Cone pairs",
        x: r_segs, y: z_segs,
        line: { color: "#9ca3af", width: 0.8 },
        hoverinfo: "skip",
        showlegend: false,
      },
    ];
  })();

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <ViewChart
          title="Conformal Mapping"
          xLabel="m [mm]" yLabel="∫r dθ [mm]"
          traces={confTraces}
          layoutExtra={{ yaxis: { scaleanchor: "x", scaleratio: 1, title: "∫r dθ [mm]", gridcolor: "#e5e7eb" } }}
        />
        <ViewChart
          title="Wrap (top view)"
          xLabel="x [mm]" yLabel="y [mm]"
          traces={wrapTraces}
          layoutExtra={{ yaxis: { scaleanchor: "x", scaleratio: 1, title: "y [mm]", gridcolor: "#e5e7eb" } }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <ViewChart
          title="Hub – Shroud"
          xLabel="r [mm]" yLabel="z [mm]"
          traces={[...hubShroudTraces, ...cutoffOverlay]}
          layoutExtra={{ yaxis: { scaleanchor: "x", scaleratio: 1, title: "z [mm]", gridcolor: "#e5e7eb" } }}
        />
        <ViewChart
          title="Meridional Cone Area Development"
          xLabel="m [mm]" yLabel="Cone area [mm²]"
          traces={coneTraces}
        />
      </div>
      {(coneMecoTraces.length > 0 || curvTraces.length > 0) && (
        <div className="grid grid-cols-2 gap-4">
          {coneMecoTraces.length > 0 && (
            <ViewChart
              title="Cone Pairs"
              xLabel="r [mm]" yLabel="z [mm]"
              traces={coneMecoTraces}
              layoutExtra={{ yaxis: { scaleanchor: "x", scaleratio: 1, title: "z [mm]", gridcolor: "#e5e7eb" } }}
            />
          )}
          {curvTraces.length > 0 && (
            <ViewChart
              title="Curvature (Porcupine)"
              xLabel="r [mm]" yLabel="z [mm]"
              traces={[
                // base curves (hub + shroud, no annotation)
                ...processed
                  .filter(l => l.name.toUpperCase().includes("HUB") || l.name.toUpperCase().includes("SHROUD"))
                  .map(l => ({
                    type: "scatter", mode: "lines", name: l.name,
                    x: l.r, y: l.z,
                    line: { color: colorByName[l.name] || LINE_COLORS[0], width: 2 },
                  })),
                ...curvTraces,
              ]}
              layoutExtra={{ yaxis: { scaleanchor: "x", scaleratio: 1, title: "z [mm]", gridcolor: "#e5e7eb" } }}
            />
          )}
        </div>
      )}
    </div>
  );
}
